import { getTreeNodesIds } from 'components/CommonComponents/GroupsTree/utils'
import keys from 'constants/keys'
import { isEmpty } from 'lodash'
import { useMemo, useState } from 'react'
import { getNamesForTooltip, getSplittedGroupNames } from '../components/NewsFeed/PostModal/utils'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'

interface IUsePostAudienceState {
  users?: string[]
  tagIds?: string[]
  groups?: string[]
  postType?: number
  selectedOptionType: string
  isCalculateGroupsChildren?: boolean
}

export const useAudienceState = ({
  users = [],
  groups = [],
  tagIds = [],
  postType,
  selectedOptionType,
  isCalculateGroupsChildren = true,
}: IUsePostAudienceState) => {
  const uid = useSelector((state: RootState) => state.auth.uid!)
  const { retailUsersObject, retail_users, tags } = useSelector((state: RootState) => state.config.config)
  const {
    groups: permittedPostGroups,
    users: permittedPostUsers,
    tags: permittedPostTagsIds,
  } = useSelector((state: RootState) => state.config.userWritePermittedContext)

  const {
    groups: permittedTaskGroups,
    users: permittedTaskUsers,
    tags: permittedTaskTagsIds,
  } = useSelector((state: RootState) => state.config.userWriteTaskPermissions)

  const isTask = selectedOptionType === 'TASK'

  const permittedGroups = isTask ? permittedTaskGroups : permittedPostGroups
  const permittedUsers = isTask ? permittedTaskUsers : permittedPostUsers
  const permittedTagsIds = isTask ? permittedTaskTagsIds : permittedPostTagsIds

  const permittedTags = useMemo(() => {
    return tags.filter((tag) => permittedTagsIds?.includes(tag.sk))
  }, [tags, permittedTagsIds])

  const getGroupsChildren = useMemo(() => {
    return groups && groups?.length > 0
      ? groups.map((group: any) => getTreeNodesIds(permittedGroups, group)).flat(1)
      : []
  }, [groups, permittedGroups])

  const [selectedGroupsIds, setSelectedGroupsIds] = useState<string[]>(
    postType !== keys.POST_TYPE.GROUP_POST && isCalculateGroupsChildren
      ? getGroupsChildren.length > 0
        ? getGroupsChildren
        : []
      : groups ?? []
  )

  const selectedGroupsIdsSet = useMemo(() => Array.from(new Set(selectedGroupsIds)), [selectedGroupsIds])

  const [selectedUsersUids, setSelectedUsersUids] = useState<string[]>(users ?? [])
  const [groupsToPayload, setGroupsToPayload] = useState<string[]>(groups && groups.length > 0 ? groups : [])
  const [selectedTagsIds, setSelectedTagsIds] = useState<string[]>(tagIds ?? [])

  const namesForTooltip = useMemo(() => {
    if (!isEmpty(permittedGroups) && retailUsersObject) {
      return getNamesForTooltip({
        groups: permittedGroups,
        retailUsersObject,
        selectedGroupsIds: selectedGroupsIdsSet,
        groupsToPayload,
        selectedUsersUids,
        selectedOptionType,
        tags: permittedTags,
        selectedTagsIds,
      })
    } else {
      return []
    }
  }, [
    groupsToPayload,
    retailUsersObject,
    selectedGroupsIdsSet,
    selectedOptionType,
    selectedTagsIds,
    selectedUsersUids,
    permittedTags,
    permittedGroups,
  ])

  const splittedGroupNames = useMemo(() => {
    return getSplittedGroupNames(namesForTooltip)
  }, [namesForTooltip])

  const filteredRerailUsersByTreeUsers = useMemo(() => {
    const sortedUsers = retail_users
      .filter((user) => permittedUsers?.includes(user.uid) && user.uid !== uid)
      .sort((a: any, b: any) => {
        const firstNameComparison = a?.first_name?.localeCompare(b?.first_name)
        if (firstNameComparison === 0) {
          return a?.last_name?.localeCompare(b?.last_name)
        }

        return firstNameComparison
      })

    return sortedUsers
  }, [permittedUsers, retail_users, uid])

  const clearAudienceState = () => {
    setSelectedUsersUids([])
    setSelectedGroupsIds([])
    setGroupsToPayload([])
    setSelectedTagsIds([])
  }

  return {
    selectedUsersUids,
    setSelectedUsersUids,
    selectedGroupsIds: selectedGroupsIdsSet,
    setSelectedGroupsIds,
    selectedTagsIds,
    setSelectedTagsIds,
    groupsToPayload,
    setGroupsToPayload,
    splittedGroupNames,
    filteredRerailUsersByTreeUsers,
    namesForTooltip,
    treeUsers: permittedUsers,
    treeGroups: permittedGroups,
    treeTags: permittedTags,
    clearAudienceState,
  }
}
